import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Spinner,
} from 'react-bootstrap';
import NotificationService, { officePushApi } from '../../Services/NotificationService';
import {
  PUSH_SUPPORT,
  currentSubscription,
  disablePush,
  enablePush,
  hasPushApis,
  idOf,
  supportState,
} from '../../Services/PushNotifications';

/**
 * "Phone notifications" — the office half.
 *
 * DEVICES, NOT PREFERENCES
 * ------------------------
 * There is deliberately no per-type switch on this card. A push is the
 * bell's twin: whatever the table below this card says you should see in the
 * bell is also pushed to the phones listed here, and a type you muted is
 * neither. That is the whole reason this is a device list rather than a
 * second set of switches — one preference system, not two, so the two can
 * never drift apart.
 *
 * So there are two ways to turn phone notifications off, and the card says
 * both: remove a device here (that phone stops), or mute a type in the table
 * (the bell and every phone stop, for that type).
 *
 * NEVER A DEAD TOGGLE
 * -------------------
 * `supportState` distinguishes the reasons push cannot work on THIS browser
 * and each gets its own true sentence — an iPhone that needs the Home
 * Screen, a permission already refused, a browser with no Web Push, and a
 * deploy whose server has no VAPID keys. None of them is offered a button.
 */

const UNAVAILABLE = {
  [PUSH_SUPPORT.IOS_NEEDS_HOME_SCREEN]: 'On an iPhone or iPad, phone notifications work only '
    + 'once this dashboard is added to the Home Screen. Add it from the Share menu in Safari, '
    + 'open it from the icon, and turn them on there.',
  [PUSH_SUPPORT.DENIED]: 'You have blocked notifications for this site, so the browser will not '
    + 'ask again. Allow them in your browser settings for this site, then reload this page.',
  [PUSH_SUPPORT.UNSUPPORTED]: 'This browser cannot receive push notifications. '
    + 'Chrome, Edge, Firefox and Safari 16.4+ can.',
  [PUSH_SUPPORT.NOT_CONFIGURED]: 'Phone notifications are not switched on for this deployment yet. '
    + 'Your dashboard bell and emails are unaffected.',
};

export default function PhoneNotifications() {
  const [state, setState] = useState(null);
  const [support, setSupport] = useState(PUSH_SUPPORT.UNSUPPORTED);
  const [thisDeviceId, setThisDeviceId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);

      const subscription = await currentSubscription();
      const next = hasPushApis()
        ? await NotificationService.getPushState()
        : { public_key: undefined, devices: [] };

      setState(next);
      setThisDeviceId(subscription ? await idOf(subscription) : null);
      setSupport(supportState({ publicKey: next.public_key, subscribed: !!subscription }));
    } catch {
      setError('Could not load your phone notification settings.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // The click IS the user gesture the browser needs to show a permission
  // prompt; it cannot be raised any other way.
  const turnOn = useCallback(async () => {
    setBusy(true);
    setError(null);

    let message = null;

    try {
      const result = await enablePush(officePushApi);

      if (!result.ok && result.reason === PUSH_SUPPORT.DENIED) {
        message = 'You dismissed or blocked the browser prompt, so nothing was turned on.';
      }
    } catch {
      message = 'Could not turn on notifications for this device.';
    }

    // Re-read FIRST, then say what happened: `refresh` clears the error so a
    // stale one does not outlive its cause, which would otherwise wipe this
    // message before anyone could read it.
    await refresh();
    setBusy(false);
    if (message) setError(message);
  }, [refresh]);

  const turnOff = useCallback(async () => {
    setBusy(true);
    setError(null);

    let message = null;

    try {
      await disablePush(officePushApi);
    } catch {
      message = 'Could not turn off notifications for this device.';
    }

    await refresh();
    setBusy(false);
    if (message) setError(message);
  }, [refresh]);

  const removeDevice = useCallback(async (deviceId) => {
    setBusy(true);
    setError(null);

    let message = null;

    try {
      // Removing the device you are sitting at should also drop this
      // browser's own subscription, or it would silently re-register on the
      // next load (syncSubscription) and look like the removal never worked.
      if (deviceId === thisDeviceId) await disablePush(officePushApi);
      else await NotificationService.unsubscribePush(deviceId);
    } catch {
      message = 'Could not remove that device.';
    }

    await refresh();
    setBusy(false);
    if (message) setError(message);
  }, [refresh, thisDeviceId]);

  if (isLoading) return null;

  const devices = state?.devices || [];
  const isOn = support === PUSH_SUPPORT.SUBSCRIBED;
  const canAsk = support === PUSH_SUPPORT.READY;

  return (
    <Card className="mb-4">
      <Card.Header className="bg-dark text-white">
        <i className="fa fa-mobile-alt me-2" />
        Phone Notifications
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-3">
          Get the same notifications on your phone that the bell shows here. Types you switch off
          below are not pushed either — there is one set of preferences, not two.
        </p>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="fa fa-exclamation-circle me-2" />
            {error}
          </Alert>
        )}

        {!isOn && !canAsk && UNAVAILABLE[support] && (
          <Alert variant="secondary" className="mb-3">
            <i className="fa fa-info-circle me-2" />
            {UNAVAILABLE[support]}
          </Alert>
        )}

        {canAsk && (
          <Button variant="primary" onClick={turnOn} disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fa fa-bell me-2" />}
            Turn on for this device
          </Button>
        )}

        {isOn && (
          <Button variant="outline-secondary" onClick={turnOff} disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fa fa-bell-slash me-2" />}
            Turn off for this device
          </Button>
        )}

        {devices.length > 0 && (
          <div className="mt-4">
            <div className="fw-semibold mb-2">Devices receiving notifications</div>
            <ul className="list-group">
              {devices.map((device) => (
                <li key={device.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>
                    {device.label}
                    {device.id === thisDeviceId && <span className="badge bg-secondary ms-2">this device</span>}
                    {device.created_at && (
                      <span className="text-muted small ms-2">
                        {`added ${new Date(device.created_at).toLocaleDateString()}`}
                      </span>
                    )}
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger p-0"
                    disabled={busy}
                    onClick={() => removeDevice(device.id)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
            <div className="text-muted small mt-2">
              Removing a device stops notifications on that phone only. To stop a whole
              category everywhere, switch it off in the table below.
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
